import {
  buildFilterKey,
  BRAND_VALUE_FILTER_TYPE,
} from "@faire/deprecated-retailer-visitor-shared/filters/filterKeys";
import { splitFiltersFromQueryParamString } from "@faire/deprecated-retailer-visitor-shared/filters/splitFiltersFromQueryParams";
import { uniqueAndSorted } from "@faire/deprecated-retailer-visitor-shared/lib/uniqueAndSorted";
import { route as categoryC1BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/brand-value/brandValue";
import { route as categoryC2BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/brand-value/brandValue";
import { route as categoryC3BrandValueRoute } from "@faire/web-api--source/routes/www/category/c1CategoryUrlId/subcategory/c2CategoryUrlId/c3CategoryUrlId/brand-value/brandValue";
import { QueryParameters } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId";
import { route as supplierC1BrandValueRoute } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId/brand-value/brandValue";
import { isTupleOfLength } from "@faire/web/common/typescriptUtils";
import { useQueryParams } from "@faire/web/ui/hooks/useQueryParams";
import qs from "query-string";
import { useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { matchPath, useHistory } from "react-router-dom";

export const useLoggedOutSuppliersPageQueryParams = () => {
  const { pathname } = useLocation();
  const history = useHistory();
  const queryParams = useQueryParams(QueryParameters.parse);
  const updateQueryParamsAndStringify =
    useUpdateQueryParamsAndStringify(queryParams);
  const { filters } = queryParams;

  const uniqueSortedFilterKeys = useMemo(() => {
    // This is to support standard query params like `?filters=k1&filters=k2`
    const normalizedFiltersQueryParams =
      (filters && isTupleOfLength(filters, 1)
        ? splitFiltersFromQueryParamString(filters[0])
        : filters) ?? [];

    return uniqueAndSorted(normalizedFiltersQueryParams);
  }, [filters]);

  const setQueryParams = (
    updater: (oldQueryParams: QueryParameters) => Partial<QueryParameters>
  ) => {
    const search = updateQueryParamsAndStringify((queryParamsInMemory) => ({
      ...queryParamsInMemory,
      ...updater(queryParamsInMemory),
    }));
    history.push({ pathname, search });
  };

  const setFilterKeys = (updater: (oldFilterKeys: string[]) => string[]) => {
    const updatedFilterKeys = uniqueAndSorted(updater(uniqueSortedFilterKeys));
    const queryString = updateQueryParamsAndStringify((oldParams) => ({
      ...oldParams,
      filters:
        updatedFilterKeys.length === 0
          ? undefined
          : [updatedFilterKeys.join(",")],
      page: undefined,
    }));

    const matchForFilterByBrandValue = matchPath<{
      brandValue: string;
    }>(pathname, {
      path: [
        categoryC1BrandValueRoute,
        supplierC1BrandValueRoute,
        categoryC2BrandValueRoute,
        categoryC3BrandValueRoute,
      ],
    });

    if (matchForFilterByBrandValue?.params.brandValue) {
      const brandValue = matchForFilterByBrandValue.params.brandValue;
      const shouldRemoveBrandValueInURL = !queryString?.includes(
        buildFilterKey(BRAND_VALUE_FILTER_TYPE, brandValue)
      );

      if (shouldRemoveBrandValueInURL) {
        history.push({
          pathname: pathname.replace(`/brand-value/${brandValue}`, ""),
          search: queryString,
        });
      }
    } else {
      history.push({ pathname: pathname, search: queryString });
    }
  };

  const clearFilters = () => {
    setFilterKeys(() => []);
  };

  return {
    queryParams,
    setQueryParams,
    setFilterKeys,
    clearFilters,
  };
};

const useUpdateQueryParamsAndStringify = (queryParams: QueryParameters) => {
  const queryParamsInMemory = useRef(queryParams);

  useEffect(() => {
    queryParamsInMemory.current = queryParams;
  }, [queryParams]);

  return (updater: (oldParams: QueryParameters) => QueryParameters): string => {
    queryParamsInMemory.current = updater(queryParamsInMemory.current);
    return qs.stringify(queryParamsInMemory.current);
  };
};
